/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

type MFASliceState = {
  email: string;
  mfaToken: string;
  oobCode?: string;
  password: string;
};

const initialState: MFASliceState = {
  email: "",
  mfaToken: "",
  oobCode: undefined,
  password: "",
};

const mfaSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "mfa",
  reducers: {
    resetMFAState: () => initialState,
    setLoginCredentials: (
      state,
      action: PayloadAction<{ email: string; password: string }>,
    ) => {
      const { email, password } = action.payload;
      state.email = email;
      state.password = password;
    },
    setMFAToken: (state, action: PayloadAction<string>) => {
      state.mfaToken = action.payload;
    },
    setOOBCode: (state, action: PayloadAction<string>) => {
      state.oobCode = action.payload;
    },
  },
});

const {
  resetMFAState: resetMFAStateAction,
  setLoginCredentials: setLoginCredentialsAction,
  setMFAToken: setMFATokenAction,
  setOOBCode: setOOBCodeAction,
} = mfaSlice.actions;

const useMFASelector = () => {
  return useAppSelector((state) => state.mfa);
};

const useMFADispatch = () => {
  const dispatch = useAppDispatch();

  const setLoginCredentials = (email: string, password: string) => {
    dispatch(setLoginCredentialsAction({ email, password }));
  };
  const setMFAToken = (mfaCode: string) => {
    dispatch(setMFATokenAction(mfaCode));
  };

  const setOOBCode = (oobCode: string) => {
    dispatch(setOOBCodeAction(oobCode));
  };

  const resetMFAState = () => {
    dispatch(resetMFAStateAction());
  };

  return {
    resetMFAState,
    setLoginCredentials,
    setMFAToken,
    setOOBCode,
  };
};

export type { MFASliceState };
export {
  resetMFAStateAction,
  setMFATokenAction,
  setOOBCodeAction,
  useMFADispatch,
  useMFASelector,
};

export default mfaSlice.reducer;
